import { APITypesV1 } from "@cur8/api-client";
import { Button, Icon24ArrowRight, Typography } from "@cur8/maneki";
import { useAssignSkinScannerMutation } from "hooks/mutations/useAssignSkinScannerMutation";
import { usePatientQuery } from "hooks/queries/usePatientQuery";
import { useSkinScannerStatusQuery } from "hooks/queries/useSkinScannerStatus";
import { useVisitQuery } from "hooks/queries/useVisitQuery";
import { readScanStateFromVisit } from "lib/readScanStateFromVisit";
import { CheckInParams } from "lib/types/checkInParameters";
import { useMemo } from "react";
import Checkmark from "render/assets/icons/96x96_check.svg?react";
import { paths } from "render/routes/paths";
import { ActionsFooter } from "render/ui/layout/ActionsFooter";
import { InformationLayout } from "render/ui/layout/InformationLayout";
import { PageFrameContent } from "render/ui/layout/PageFrameContent";
import { List } from "render/ui/presentation/List";
import { SpinningLoader } from "render/ui/presentation/SpinningLoader";
import { Link } from "render/ui/trigger/Link";
import { BailView } from "render/views/BailView";
import styles from "./styles.module.sass";

export function AssignToSkinScanner({
  goTo,
  patientId,
  visitId,
}: AssignToSkinScannerProps) {
  const { data: patient } = usePatientQuery({ patientId });
  const { data: visit } = useVisitQuery({ patientId, visitId });
  const assignSkinScannerMutation = useAssignSkinScannerMutation();
  const skinScannerStatusQuery = useSkinScannerStatusQuery({
    patientId: patientId,
    visitId: visitId,
  });

  const skinScannerStatus = useMemo(() => {
    return skinScannerStatusQuery.data?.status;
  }, [skinScannerStatusQuery.data?.status]);
  const statusLabel = skinScannerStatus
    ? statusLabels[skinScannerStatus]
    : "Could not get status";
  const canAssign = useMemo(() => {
    return (
      skinScannerStatus &&
      skinScannerStatus !==
        APITypesV1.DeviceAssignmentStatus.OtherMemberAssigned
    );
  }, [skinScannerStatus]);

  const skinScan = readScanStateFromVisit("skin", visit);
  const alreadyHasSkinScan = skinScan != null;

  const loading = assignSkinScannerMutation.isPending;
  const error = assignSkinScannerMutation.isError;
  const success = assignSkinScannerMutation.isSuccess;

  if (!patient) {
    return <BailView title="Could not fetch member" />;
  }

  const text = alreadyHasSkinScan
    ? `Are you sure you want to reassign ${patient.name?.displayName} to the skin scanner?`
    : `Assign ${patient.name?.displayName} to the skin scanner when they are ready`;
  const buttonText = alreadyHasSkinScan
    ? "Reassign to skin scanner"
    : "Assign to skin scanner";

  return (
    <PageFrameContent>
      <section className={styles.base}>
        <section
          className={styles.transition}
          data-active={!loading && !error && !success}
          aria-hidden={loading || error || success}
        >
          <InformationLayout
            content={
              <section className={styles.layout}>
                <div className={styles.text}>
                  <Typography variant="display-s">{text}</Typography>
                  {alreadyHasSkinScan ? (
                    <Typography variant="title-s" color="subtle">
                      {patient.name?.displayName} already had a skin scan today.
                    </Typography>
                  ) : null}
                </div>
                <List>
                  <List.Item>
                    <div className={styles.item}>
                      <Typography variant="title-m">Skin scanner</Typography>
                      {statusLabel ? (
                        <StatusBadge error={!canAssign} label={statusLabel} />
                      ) : undefined}
                    </div>
                  </List.Item>
                </List>
              </section>
            }
            buttons={
              <Button
                state={!canAssign ? "disabled" : undefined}
                onPress={() => {
                  assignSkinScannerMutation.mutate(
                    {
                      patientId: patientId,
                      visitId: visitId,
                    },
                    {
                      onSuccess: () =>
                        setTimeout(
                          () =>
                            goTo(
                              paths.checkInSuccess.url({
                                patientId,
                                visitId,
                              })
                            ),
                          3000
                        ),
                    }
                  );
                }}
                fillContainer
                icon={<Icon24ArrowRight />}
              >
                {buttonText}
              </Button>
            }
          />
          <Link
            className={styles.skipLink}
            onPress={() =>
              goTo(paths.checkInSuccess.url({ patientId, visitId }))
            }
          >
            Skip skin scan
          </Link>
        </section>
        <section
          className={styles.transition}
          data-active={loading || success}
          aria-hidden={!loading || !success}
        >
          <div className={styles.loader}>
            <SpinningLoader success={success} />
            <div className={styles.loaderText} data-active={success}>
              <Typography variant="title-s">Member assigned</Typography>
              <Typography variant="title-s">to skin scanner</Typography>
            </div>
            <div className={styles.checkmark} data-active={success}>
              <Checkmark />
            </div>
          </div>
        </section>
        <section
          className={styles.transition}
          data-active={error}
          data-bottom
          aria-hidden={!error}
        >
          <InformationLayout
            content={
              <section className={styles.title}>
                <Typography variant="display-s">
                  Couldn't assign member to the skin scanner
                </Typography>
                <Typography variant="body-m" color="subtle">
                  Please try again, or cancel and proceed with the other
                  examinations
                </Typography>
              </section>
            }
            buttons={
              <ActionsFooter
                left={
                  <Button
                    variant="secondary"
                    onPress={() => {
                      goTo(paths.checkInSuccess.url({ patientId, visitId }));
                    }}
                    fillContainer
                  >
                    Cancel
                  </Button>
                }
                right={
                  <Button
                    state={loading ? "loading" : undefined}
                    onPress={() => {
                      assignSkinScannerMutation.mutate(
                        {
                          patientId: patientId,
                          visitId: visitId,
                        },
                        {
                          onSuccess: () =>
                            setTimeout(
                              () =>
                                goTo(
                                  paths.checkInSuccess.url({
                                    patientId,
                                    visitId,
                                  })
                                ),
                              3000
                            ),
                        }
                      );
                    }}
                    fillContainer
                    icon={<Icon24ArrowRight />}
                  >
                    Assign to skin scanner
                  </Button>
                }
              />
            }
          />
        </section>
      </section>
    </PageFrameContent>
  );
}

type AssignToSkinScannerProps = {
  goTo(url: string): void;
} & CheckInParams;

const statusLabels = {
  [APITypesV1.DeviceAssignmentStatus.CanAssign]: "Free",
  [APITypesV1.DeviceAssignmentStatus.AssignedToMember]: "Already assigned",
  [APITypesV1.DeviceAssignmentStatus.OtherMemberAssigned]: "Occupied",
} as const;

function StatusBadge({ error, label }: StatusBadgeProps) {
  return (
    <div className={styles.status} data-error={error}>
      <Typography variant="eyebrow-m" as="span">
        {label}
      </Typography>
    </div>
  );
}

type StatusBadgeProps = {
  error: boolean | undefined;
  label: string;
};
